/**
 * Contrato canônico do owner web (DI2-1) — ÚNICA fonte dos schemas e tipos das rotas `/web/*`.
 *
 * Duas superfícies consomem este arquivo:
 *   - `apps/api` — `src/domain/owner-envelope/contracts.ts` apenas RE-EXPORTA daqui, nada é
 *     redefinido lá (antes desta rodada as duas cópias existiam e já divergiam);
 *   - o browser — importa por **subpath** (`@argink/shared/owner-web`), nunca pelo index: o
 *     index reexporta `webhook-hmac`, que puxa `node:crypto` para dentro do bundle.
 *
 * Por isso a única dependência aqui é `zod`. Nada de `node:*`, nada de Prisma.
 *
 * Casing: snake_case, igual ao resto da API (DI2-2). A tradução para camelCase é
 * responsabilidade do adapter do front, não deste contrato.
 */
import { z } from 'zod';
import {
  STAMP_HEIGHT_MAX,
  STAMP_HEIGHT_MIN,
  STAMP_WIDTH_MAX,
  STAMP_WIDTH_MIN,
  zVisualPlacement,
  type VisualPlacement,
} from './dtos';

/**
 * Carimbo visível (D-027, gate aprovado na D-038): o posicionamento é o MESMO schema da `/v1`
 * (`zVisualPlacement`), importado — nunca redefinido aqui. Duas superfícies com dois schemas
 * para a mesma coisa é como nascem divergências caladas, e o front precisa do tipo para montar
 * o payload. `dtos.ts` só depende de `zod`, então o subpath continua seguro para o browser
 * (GATE DI2-A — `test/unit/browser-safety.test.ts` caminha este grafo).
 */
export { zVisualPlacement };
/** Faixa de tamanho do carimbo — mesma do `/v1`, importada (nunca redefinida). */
export { STAMP_WIDTH_MIN, STAMP_WIDTH_MAX, STAMP_HEIGHT_MIN, STAMP_HEIGHT_MAX };
export type { VisualPlacement };

export const OWNER_WEB_MAX_PDF_BYTES = 20 * 1024 * 1024;
export const OWNER_WEB_DEFAULT_PAGE_SIZE = 20;
export const OWNER_WEB_MAX_PAGE_SIZE = 100;

/** Avançada está FORA do MVP na CRIAÇÃO (mandato §1) — o contrato de criação nem admite o
 *  valor. Intocado nesta correção (Correção A / S1): continua recusando `avancada`. Para
 *  LEITURA (Prisma e UI já suportam os três níveis), ver `zOwnerWebSignatureLevelRead` abaixo —
 *  os dois tipos NUNCA convergem: criação é o subconjunto restrito, leitura é o valor real. */
export const zOwnerWebSignatureLevel = z.enum(['simples', 'qualificada']);
/**
 * Nível de assinatura na LEITURA (Correção A / S1) — três valores, espelhando o enum
 * `SignatureLevel` do Prisma (`simples | avancada | qualificada`), que a UI já suporta. Antes
 * desta correção o contrato de LEITURA emprestava `zOwnerWebSignatureLevel` (só 2 valores) e o
 * mapper (`apps/api/src/domain/owner-envelope/mapping.ts`) escondia a divergência com casts —
 * `avancada` chegava a existir no banco e na UI sem existir no contrato. Aplicado em
 * `OwnerWebParticipantView.required_signature_level`/`actual_signature_level` e em
 * `zOwnerWebEnvelopeView`. NUNCA usado na criação — `zOwnerWebCreateEnvelopePayload` continua
 * recusando `avancada` via `zOwnerWebSignatureLevel`.
 */
export const zOwnerWebSignatureLevelRead = z.enum(['simples', 'avancada', 'qualificada']);
export const zOwnerWebParticipantRole = z.enum(['signer', 'observer']);
export const zOwnerWebDeadlinePreset = z.enum(['24h', '3d', '7d', '15d', '30d', 'custom']);
export const zOwnerWebEnvelopeStatus = z.enum([
  'draft',
  'ready',
  'in_progress',
  'completed',
  'declined',
  'expired',
  'cancelled',
]);
/**
 * Os 6 estados reais de `SignerStatus` no banco. `notified` e `in_session` NÃO são detalhe
 * interno: são "foi convidado" e "está assinando agora", o que o acompanhamento precisa mostrar.
 * Quem mapeia isso para linguagem humana é o front, de forma exaustiva (DI2-3).
 */
export const zOwnerWebParticipantStatus = z.enum([
  'pending',
  'notified',
  'in_session',
  'signed',
  'declined',
  'expired',
]);

export const zOwnerWebParticipantInput = z
  .object({
    name: z.string().trim().min(1).max(255),
    email: z.string().email().max(320),
    cpf: z.string().regex(/^\d{11}$/).nullable(),
    role: zOwnerWebParticipantRole,
    signature_level: zOwnerWebSignatureLevel.nullable(),
    stage: z.number().int().min(1).nullable(),
    /**
     * Carimbo visível deste signatário (D-027) — **opt-in**. Ausente OU `null` significam a
     * mesma coisa: sem carimbo, o comportamento default de sempre.
     *
     * `.nullish()`, e não o `.nullable()` obrigatório dos vizinhos, de propósito: `cpf`,
     * `signature_level` e `stage` são decisões que o wizard TEM de tomar para cada participante
     * (por isso exigem `null` explícito — omitir é esquecimento). O carimbo é capacidade
     * opcional acrescentada a um contrato já em uso: exigir a chave quebraria todo payload
     * existente sem ganhar semântica nenhuma, e recusar `null` puniria um cliente que preenche
     * os campos de forma uniforme. Quem não pede carimbo continua enviando exatamente o mesmo
     * JSON de antes.
     *
     * Simetria com `/v1`: lá o campo só é aceito em documento `pades` (`routes/envelopes.ts`
     * recusa com 400 em cades/xades). Aqui a checagem não se repete porque `/web` **só** aceita
     * PDF — `validateOwnerPdf` (magic bytes + MIME + extensão) roda antes deste parse, então
     * todo documento desta superfície já é pades por construção.
     */
    visual: zVisualPlacement.nullish(),
  })
  .strict()
  .superRefine((participant, context) => {
    if (participant.role === 'signer') {
      if (participant.signature_level === null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['signature_level'],
          message: 'signature_level e obrigatorio para signer',
        });
      }
      if (participant.stage === null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['stage'],
          message: 'stage e obrigatorio para signer',
        });
      }
      // CPF por MODALIDADE (decisão do Felipe, mandato §10). Na qualificada o CPF é o vínculo
      // entre o participante convidado e o titular do certificado ICP-Brasil (D-035): sem ele
      // não existe o que conferir na hora de assinar, então o contrato recusa antes de criar o
      // envelope — fail-closed, não "cobra depois". Na simples o CPF é OPCIONAL: quando vem, é
      // gravado e entra na evidência pelo `signer_added`; quando não vem, a manifestação segue
      // provada por e-mail + OTP, sem inventar identidade que ninguém verificou.
      if (participant.signature_level === 'qualificada' && participant.cpf === null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['cpf'],
          message: 'cpf e obrigatorio para assinatura qualificada',
        });
      }
      // O carimbo é por SIGNATÁRIO e independe do nível (Simples e Qualificada podem ter) —
      // mas depende do CPF: o texto desenhado é "Assinado digitalmente por <NOME> / CPF
      // ***.456.789-**" (`domain/visual-stamp.ts:buildStampText`). Sem CPF não existe carimbo
      // para desenhar, e o caminho de assinatura falha explícito na hora de selar
      // (`routes/signers.ts:resolveEngineVisual` lança quando `cpf === null`). Recusar aqui é
      // fail-closed: o envelope nem nasce em vez de morrer na cerimônia, com o signatário na
      // tela. Na Qualificada a regra acima já garante o CPF; quem isto alcança é a Simples,
      // onde o CPF é opcional.
      if (participant.visual !== null && participant.visual !== undefined && participant.cpf === null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['cpf'],
          message: 'cpf e obrigatorio para carimbo visivel (o carimbo exibe o cpf mascarado)',
        });
      }
      return;
    }

    // Observer não assina, não bloqueia stage e não consome unidade (mandato §4) — coletar o CPF
    // dele seria dado pessoal sem finalidade probatória. Exige `null` EXPLÍCITO em vez de campo
    // ausente: omitir é esquecimento, `null` é decisão.
    if (participant.cpf !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cpf'],
        message: 'observer nao informa cpf',
      });
    }
    if (participant.signature_level !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['signature_level'],
        message: 'observer nao possui modalidade de assinatura',
      });
    }
    if (participant.stage !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stage'],
        message: 'observer fica fora dos stages',
      });
    }
    // Mesma regra da `/v1` (`zAddSignerRequest`, dtos.ts): observer não assina, então não tem
    // aparência de assinatura para carimbar. Aceita ausente ou `null` (as duas formas de "sem
    // carimbo"); qualquer posicionamento é recusado.
    if (participant.visual !== null && participant.visual !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visual'],
        message: 'observer nao possui carimbo visivel',
      });
    }
  });

export const zOwnerWebCreateEnvelopePayload = z
  .object({
    participants: z.array(zOwnerWebParticipantInput).min(1).max(100),
    deadline_preset: zOwnerWebDeadlinePreset,
    expires_at: z.string().datetime({ offset: true }),
    timezone: z.string().trim().min(1).max(64),
  })
  .strict()
  .superRefine((payload, context) => {
    const signers = payload.participants.filter((participant) => participant.role === 'signer');
    if (signers.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['participants'],
        message: 'informe ao menos um signer',
      });
      return;
    }
    const stages = [...new Set(signers.map((signer) => signer.stage!))].sort((a, b) => a - b);
    const contiguous = stages.every((stage, index) => stage === index + 1);
    if (!contiguous) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['participants'],
        message: 'stages dos signers devem ser contiguos e iniciar em 1',
      });
    }
  });

export const zOwnerWebStartEnvelopeRequest = z
  .object({
    confirmation: z.literal(true),
    expectedUpdatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

/**
 * Reenvio de notificação é sempre POR PARTICIPANTE (a UI oferece "Reenviar" ao lado de cada
 * signatário pendente). Alvo obrigatório de propósito: body vazio viraria um disparo em massa
 * silencioso, que ninguém pediu e que o rate-limit por envelope não distingue.
 */
export const zOwnerWebResendRequest = z
  .object({
    participantId: z.string().uuid(),
  })
  .strict();

export type OwnerWebSignatureLevel = z.infer<typeof zOwnerWebSignatureLevel>;
/** Tipo de leitura (Correção A / S1) — três valores; ver `zOwnerWebSignatureLevelRead` acima. */
export type OwnerWebSignatureLevelRead = z.infer<typeof zOwnerWebSignatureLevelRead>;
export type OwnerWebParticipantRole = z.infer<typeof zOwnerWebParticipantRole>;
export type OwnerWebDeadlinePreset = z.infer<typeof zOwnerWebDeadlinePreset>;
export type OwnerWebEnvelopeStatus = z.infer<typeof zOwnerWebEnvelopeStatus>;
export type OwnerWebParticipantStatus = z.infer<typeof zOwnerWebParticipantStatus>;
export type OwnerWebParticipantInput = z.infer<typeof zOwnerWebParticipantInput>;
export type OwnerWebCreateEnvelopePayload = z.infer<typeof zOwnerWebCreateEnvelopePayload>;
export type OwnerWebStartEnvelopeRequest = z.infer<typeof zOwnerWebStartEnvelopeRequest>;
export type OwnerWebResendRequest = z.infer<typeof zOwnerWebResendRequest>;

export interface OwnerWebParticipantView {
  id: string;
  name: string;
  /** `null` depois do apagamento de PII (D-029) — a UI trata ausência, não assume string. */
  email: string | null;
  role: OwnerWebParticipantRole;
  stage: number | null;
  required_signature_level: OwnerWebSignatureLevelRead | null;
  actual_signature_level: OwnerWebSignatureLevelRead | null;
  status: OwnerWebParticipantStatus;
  created_at: string;
  updated_at: string;
  channel_verified_at: string | null;
  signed_at: string | null;
  decline_reason: string | null;
  /**
   * Carimbo visível configurado para este participante (D-027) — `null` quando não há, que é o
   * default. Na LEITURA o campo é sempre emitido (nunca omitido): ausência de chave e "sem
   * carimbo" seriam indistinguíveis para quem lê, e a tela precisa afirmar com certeza que o
   * participante **não** terá carimbo. Observer é sempre `null` (o contrato de entrada recusa).
   */
  visual: VisualPlacement | null;
}

/**
 * Contrato do detalhe (R-UX02-P02/L3B-A). `ceremony_mode` atravessa exatamente como persistido
 * (os quatro valores do Prisma `Envelope.ceremonyMode`), sem derivar um `workflow` lossy.
 * `quorum` só é não-nulo quando persistido (tipicamente `parallel_quorum`).
 * `document: null` = zero documentos (estado válido, HTTP 200). Cardinalidade > 1 é recusada
 * no servidor (fail-closed, D-105) — nunca chega a este tipo.
 */
export interface OwnerWebEnvelopeView {
  id: string;
  status: OwnerWebEnvelopeStatus;
  ceremony_mode: 'unitary' | 'sequential' | 'parallel_quorum' | 'batch_async';
  quorum: number | null;
  /**
   * Etapa liberada AGORA, derivada pelo backend (DI2-4). Nunca calculada no cliente: derivar
   * ali reimplementaria a regra de gating e permitiria à UI discordar do backend sobre quem
   * pode assinar. `null` = nenhuma etapa corrente. Regra completa em
   * `apps/api/src/domain/owner-envelope/mapping.ts`.
   */
  current_stage: number | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  document: null | {
    id: string;
    name: string | null;
    format: 'pades' | 'cades' | 'xades';
    hash_sha256: string;
    page_count: number | null;
    created_at: string;
    signed: boolean;
  };
  participants: OwnerWebParticipantView[];
}

/** Espelho ZOD de `OwnerWebEnvelopeView` (paridade com a interface, lane B consome este schema). */
export const zOwnerWebEnvelopeView = z.object({
  id: z.string().uuid(),
  status: zOwnerWebEnvelopeStatus,
  ceremony_mode: z.enum(['unitary', 'sequential', 'parallel_quorum', 'batch_async']),
  quorum: z.number().int().nullable(),
  current_stage: z.number().int().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  expires_at: z.string().nullable(),
  document: z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    format: z.enum(['pades', 'cades', 'xades']),
    hash_sha256: z.string(),
    page_count: z.number().int().nullable(),
    created_at: z.string(),
    signed: z.boolean(),
  }).nullable(),
  participants: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().email().nullable(),
    role: zOwnerWebParticipantRole,
    stage: z.number().int().nullable(),
    required_signature_level: zOwnerWebSignatureLevelRead.nullable(),
    actual_signature_level: zOwnerWebSignatureLevelRead.nullable(),
    status: zOwnerWebParticipantStatus,
    created_at: z.string(),
    updated_at: z.string(),
    channel_verified_at: z.string().nullable(),
    signed_at: z.string().nullable(),
    decline_reason: z.string().nullable(),
    visual: zVisualPlacement.nullable(),
  })),
}).strict();

/**
 * Shape ORIGINAL do Dashboard (R-UX02-P01/D-100) — preservado como estava ANTES de
 * R-UX02-P02/L3A-1. Correção de Sol (L3A-2a, defeito 1): a L3A-1 tinha acrescentado
 * `document_id?`/`last_activity_at?` aqui, como campos OPCIONAIS, só para não quebrar o
 * typecheck de `GET /web/dashboard` e das fixtures do web enquanto a lista ainda não tinha
 * tipo próprio. Campo opcional num contrato é convite para consumidor que não lê o valor —
 * por isso a superfície da LISTA ganhou tipo PRÓPRIO (`OwnerWebEnvelopeListItem`, abaixo) em
 * vez de afrouxar este aqui. `GET /web/dashboard` (`toOwnerEnvelopeSummary`,
 * `domain/owner-envelope/mapping.ts`) volta a emitir EXATAMENTE este shape — nada a mais.
 */
export interface OwnerWebEnvelopeSummary {
  id: string;
  status: OwnerWebEnvelopeStatus;
  document_name: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  signer_count: number;
  observer_count: number;
  signed_count: number;
}

/**
 * Item de `GET /web/envelopes` (R-UX02-P02/L3A-1, endurecido em L3A-2a/defeito 1) — estende o
 * resumo do Dashboard com os dois campos que só a lista global consome. NENHUM dos dois é
 * opcional: quem lê este tipo é obrigado a lidar com ambos.
 */
export interface OwnerWebEnvelopeListItem extends OwnerWebEnvelopeSummary {
  /**
   * `null` quando o envelope não tem documento (R-UX02-P02/L3A-1 — 1.400 de 6.480 envelopes
   * medidos no banco real). Existe ao lado de `document_name` só para DESAMBIGUAR o que
   * `document_name: null` sozinho não conseguia dizer: "documento sem nome" (documento existe,
   * `Document.name IS NULL` — o caso COMUM, 83,2% dos documentos) e "sem documento nenhum"
   * colapsavam no mesmo `null`. Com `document_id` a superfície pode decidir: `document_id`
   * presente + `document_name` null ⇒ "Documento sem nome"; `document_id` null ⇒ "Envio sem
   * documento associado". Cardinalidade > 1 nunca chega aqui — `mapping.ts` recusa explícito
   * antes (nunca escolhe `documents[0]` em silêncio). Combinação `document_id === null &&
   * document_name !== null` é IMPOSSÍVEL — os dois vêm do MESMO documento resolvido no mapping,
   * e `zOwnerWebEnvelopeListItem` (abaixo) a recusa também no schema, em defesa de profundidade.
   */
  document_id: string | null;
  /**
   * Última movimentação conhecida do envelope (R-UX02-P02/L3A-1) — o maior entre
   * `Envelope.updatedAt` e o `updatedAt` de cada `Signer`, calculado no SERVIDOR
   * (`domain/owner-envelope/mapping.ts`). É "last touched", NUNCA data de conclusão,
   * cancelamento ou recusa — o schema não tem `completed_at`/`declined_at`, e este campo não
   * finge que tem.
   */
  last_activity_at: string;
}

/**
 * GET /web/envelopes (R-UX02-P02/L3A-1 — lista global real, lado servidor; endurecido em
 * L3A-2a/defeito 1). `total`, `total_filtered` e `total_returned` vêm do MESMO snapshot lógico
 * da resposta (mesma transação `$forTenant`, sob RLS, em `REPEATABLE READ` desde L3A-2a —
 * ver `domain/owner-envelope/service.ts`):
 *   - `total` — todos os envelopes visíveis ao tenant, ANTES de `status` e `q`;
 *   - `total_filtered` — os que satisfazem `status`/`q`, ANTES do cursor;
 *   - `total_returned` — quantidade efetivamente devolvida nesta página (`items.length`).
 * `items.length` PODE validar `total_returned` — nunca substitui `total` nem `total_filtered`
 * (essa é a razão de existir: a lista de hoje não tinha nenhuma contagem autoritativa e a busca
 * era um filtro local sobre a página já carregada, com aparência de busca global).
 *
 * NENHUM dos cinco campos novos (`document_id`/`last_activity_at` em cada item, e os três
 * contadores aqui) é opcional — correção de Sol (L3A-2a/defeito 1) sobre a versão `?:` da
 * L3A-1. `apps/web` (`apps/web/src/lib/api/adapters.ts` e `apps/web/test/unit/adapters.test.ts`)
 * ainda consome o shape ANTIGO deste tipo (sem os três contadores, `items` como
 * `OwnerWebEnvelopeSummary[]`) — ainda NÃO migrado (migração é a L3A-2b) — e portanto deixa de
 * typecheckar contra este tipo endurecido até essa migração acontecer. É consequência conhecida
 * e aceita desta correção, não uma regressão de runtime: o schema zod do lado do browser
 * (`ownerWebEnvelopeSummarySchema`) não é `.strict()` e ignora os campos novos sem quebrar.
 */
export interface OwnerWebEnvelopeList {
  items: OwnerWebEnvelopeListItem[];
  next_cursor: string | null;
  total: number;
  total_filtered: number;
  total_returned: number;
}

/**
 * Espelho ZOD de `OwnerWebEnvelopeSummary` (DI2-1 continua valendo: só se lê/escreve este shape
 * por aqui). Existe porque `GET /web/dashboard` (R-UX02-P01/D-100) precisa de um schema que o
 * browser possa usar para VALIDAR a resposta — a interface TS sozinha não sobrevive ao runtime.
 * `.strict()`: campo ausente ou extra falha o parse, não só o `tsc`. Sem `document_id`/
 * `last_activity_at` — esses só existem em `OwnerWebEnvelopeListItem`/`zOwnerWebEnvelopeListItem`
 * (ver abaixo, L3A-2a/defeito 1).
 */
export const zOwnerWebEnvelopeSummary = z
  .object({
    id: z.string().uuid(),
    status: zOwnerWebEnvelopeStatus,
    document_name: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    expires_at: z.string().nullable(),
    signer_count: z.number().int().min(0),
    observer_count: z.number().int().min(0),
    signed_count: z.number().int().min(0),
  })
  .strict();

/**
 * Espelho ZOD de `OwnerWebEnvelopeListItem` (R-UX02-P02/L3A-2a/defeito 1). Estende o schema do
 * resumo com os dois campos obrigatórios da lista, `.strict()` de novo (o `.extend` do zod não
 * reafirma isso sozinho) e recusa em runtime a combinação impossível `document_id === null &&
 * document_name !== null` — defesa de profundidade sobre a garantia que já vem do mapping.
 */
export const zOwnerWebEnvelopeListItem = zOwnerWebEnvelopeSummary
  .extend({
    document_id: z.string().uuid().nullable(),
    last_activity_at: z.string(),
  })
  .strict()
  .superRefine((item, context) => {
    if (item.document_id === null && item.document_name !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['document_id'],
        message: 'document_id nulo com document_name preenchido e uma combinacao impossivel',
      });
    }
  });

/**
 * Espelho ZOD de `OwnerWebEnvelopeList` (R-UX02-P02/L3A-2a/defeito 1) — `.strict()` na resposta
 * inteira (campo novo ou ausente falha o parse) e as invariantes numéricas do contrato (mandato
 * do gate TDD): `total_returned` tem que bater com `items.length`, nunca pode passar de
 * `total_filtered`, que por sua vez nunca pode passar de `total`.
 */
export const zOwnerWebEnvelopeList = z
  .object({
    items: z.array(zOwnerWebEnvelopeListItem),
    next_cursor: z.string().nullable(),
    total: z.number().int().min(0),
    total_filtered: z.number().int().min(0),
    total_returned: z.number().int().min(0),
  })
  .strict()
  .superRefine((list, context) => {
    if (list.total_returned !== list.items.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total_returned'],
        message: 'total_returned tem que ser exatamente items.length',
      });
    }
    if (list.total_returned > list.total_filtered) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total_returned'],
        message: 'total_returned nao pode exceder total_filtered',
      });
    }
    if (list.total_filtered > list.total) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total_filtered'],
        message: 'total_filtered nao pode exceder total',
      });
    }
  });

/**
 * GET /web/dashboard (R-UX02-P01/D-100 — L2-DATA). Read model dedicado do Dashboard: uma
 * chamada HTTP, métricas exatas via `COUNT(*)` no Postgres (nunca `.length` de uma página
 * capada), nunca calculadas no browser a partir de `GET /web/envelopes`. Regra completa —
 * autoridade de cada KPI, prioridade de "Requer atenção", janelas de 48h/7d — em
 * `apps/api/src/domain/owner-dashboard/service.ts` e `docs/ux/R-UX02-P01-CONTRATO.md`.
 */
export const zOwnerWebDashboardMetrics = z
  .object({
    in_progress: z.number().int().min(0),
    due_within_48h: z.number().int().min(0),
    completed: z.number().int().min(0),
    attention: z.number().int().min(0),
  })
  .strict();

/**
 * Motivo de um envelope aparecer em "Requer sua atenção". `overdue`/`due_soon` são os dois
 * lados do mesmo tier de prazo (recorte por `expires_at` relativo a `now`); os demais espelham
 * o `status` do envelope. Cada envelope entra com **um** `kind` (o de maior prioridade — ver
 * `resolveAttentionKindPriority` no domínio), nunca mais de um.
 */
export const zOwnerWebDashboardAttentionKind = z.enum([
  'declined',
  'expired',
  'overdue',
  'due_soon',
  'ready',
  'stalled',
]);

/**
 * O que o campo `at` representa — nunca inferido como evento de assinatura (invariante da
 * rodada). `expires_at` é o campo real de vencimento; `updated_at` é o "last touched" do
 * envelope, honesto sobre não ser um `declined_at`/`ready_at` que não existe no schema.
 */
export const zOwnerWebDashboardAtKind = z.enum(['expires_at', 'updated_at']);

export const zOwnerWebDashboardAttentionItem = z
  .object({
    envelope: zOwnerWebEnvelopeSummary,
    kind: zOwnerWebDashboardAttentionKind,
    at: z.string(),
    at_kind: zOwnerWebDashboardAtKind,
  })
  .strict();

export const zOwnerWebDashboardRecentItem = z
  .object({
    envelope: zOwnerWebEnvelopeSummary,
  })
  .strict();

export const zOwnerWebDashboardResponse = z
  .object({
    generated_at: z.string(),
    metrics: zOwnerWebDashboardMetrics,
    attention_items: z.array(zOwnerWebDashboardAttentionItem).max(5),
    recent_items: z.array(zOwnerWebDashboardRecentItem).max(6),
  })
  .strict();

export type OwnerWebDashboardMetrics = z.infer<typeof zOwnerWebDashboardMetrics>;
export type OwnerWebDashboardAttentionKind = z.infer<typeof zOwnerWebDashboardAttentionKind>;
export type OwnerWebDashboardAtKind = z.infer<typeof zOwnerWebDashboardAtKind>;
export type OwnerWebDashboardAttentionItem = z.infer<typeof zOwnerWebDashboardAttentionItem>;
export type OwnerWebDashboardRecentItem = z.infer<typeof zOwnerWebDashboardRecentItem>;
export type OwnerWebDashboardResponse = z.infer<typeof zOwnerWebDashboardResponse>;

/**
 * POST /web/envelopes/:id/validate (D-063/#100 — ação "Validar documento assinado").
 * Valida o ARTEFATO ASSINADO ARMAZENADO via client interno do motor (mesmo caminho da
 * conclusão — sem quota de tenant, sem upload). Só envelope `completed`. Tri-state
 * HONESTO: `indeterminate` significa "não foi possível confirmar toda a cadeia" — a UI
 * nunca o apresenta como inválido, nem promete "aprovado" por autoridade nenhuma.
 * A mesma shape é devolvida pela rota irmã do signatário
 * (POST /v1/envelopes/:id/validate-signed, escopo `eid` da allowlist SST).
 */
export interface OwnerWebValidateResult {
  result: 'valid' | 'invalid' | 'indeterminate';
  indication: string;
  sub_indication: string | null;
  signature_count: number;
  checked_at: string;
}

/**
 * Regime de confiança sob o qual o artefato foi validado (D-069).
 *
 * `icp_brasil` — assinatura ICP-Brasil validada pelo DSS (perfil AD-R*, cadeia de certificação
 * ICP-Brasil de verdade). `internal_seal` — SELO INTERNO da plataforma: raiz privada dedicada do
 * ARGINK, que **não é ICP-Brasil** e não produz nenhum perfil AD-R*. Confundir os dois é a fuga
 * semântica que o achado P1-2 da auditoria adversarial R1 pegou: nem o manifesto imutável nem a
 * copy da tela podem atribuir perfil/cadeia ICP a um documento que só tem selo interno.
 */
export type SignatureValidationRegime = 'icp_brasil' | 'internal_seal';

/**
 * Indicações emitidas pelo validador de SELO INTERNO (`domain/stored-artifact-validation.ts` e
 * `domain/validation-snapshot.ts` no gateway). São o marcador de regime que atravessa o contrato
 * HTTP: `OwnerWebValidateResult.indication` já viaja para o browser, então a UI deriva o regime
 * daqui em vez de adivinhar pelo nível declarado do envelope (que pode ser misto).
 */
export const INTERNAL_SEAL_INDICATION_VALID = 'INTERNAL_SEAL_VALID';
export const INTERNAL_SEAL_INDICATION_INVALID = 'INTERNAL_SEAL_INVALID';

/** Prefixo reservado às indicações do selo interno — qualquer `INTERNAL_SEAL_*` futuro continua
 *  sendo selo interno, nunca ICP. */
const INTERNAL_SEAL_INDICATION_PREFIX = 'INTERNAL_SEAL_';

/**
 * Regime a partir da `indication` devolvida pela validação do artefato assinado.
 *
 * Só `INTERNAL_SEAL_*` é selo interno; qualquer outra indicação (`TOTAL_PASSED`,
 * `INDETERMINATE`, …) vem do validador DSS/ICP-Brasil.
 */
export function validationRegimeFromIndication(indication: string): SignatureValidationRegime {
  return indication.startsWith(INTERNAL_SEAL_INDICATION_PREFIX) ? 'internal_seal' : 'icp_brasil';
}
